import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import * as api from "api"
import AnimatedNumber, { AnimatedNumberProvider } from "common/AnimatedNumber"
import { memo } from "react"
import Shared from "theme/shared"

const flex = css`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
`

const title = (p) => css`
  flex-basis: 100%;
  font-weight: bold;
  font-size: 16px;
  ${p.theme.breakpoints.up("md")} {
    font-size: 20px;
  }
`

const unit = (p) => css`
  font-size: 12px;
  color: ${p.theme.colors.bluishGrey};
  ${p.theme.breakpoints.up("md")} {
    font-size: 16px;
  }
`

const Styled = {
  Wrapper: styled.div`
    ${flex}
    align-items: center;
    gap: 20px;
    color: ${(p) => p.theme.colors.marine};
  `,
  Divider: styled(Shared.Divider)`
    flex-basis: 100%;
  `,
  Up: {
    Gauge: styled.div`
      ${flex}
      flex-basis: 100%;
      align-items: baseline;
      gap: 5px;
      white-space: nowrap;
    `,
    Title: styled(Typography)`
      ${title}
      font-size: 20px;
    `,
    Value: styled(Typography)`
      font-size: 35px;
      .slash {
        margin-left: -3px;
        margin-right: 3px;
      }

      .slash,
      b {
        font-size: 49px;
      }

      ${(p) => p.theme.breakpoints.up("md")} {
        font-size: 48px;
        .slash,
        b {
          font-size: 67px;
        }
      }
    `,
    Unit: styled(Typography)`
      font-size: 17px;
      ${(p) => p.theme.breakpoints.up("md")} {
        font-size: 24px;
      }
    `,
  },
  bottomWrapper: styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    width: 100%;
  `,
  Right: {
    Gauge: styled.div`
      ${flex}
      flex-basis: calc(50% - 10px);
      flex-direction: column;
      justify-items: center;
      gap: 5px;
    `,
    Title: styled(Typography)`
      ${title}
    `,
    Value: styled(Typography)`
      font-size: 27px;
      line-height: 49px;
      white-space: nowrap;
      ${(p) => p.theme.breakpoints.up("md")} {
        font-size: 44px;
      }
    `,
    Unit: styled(Typography)`
      ${unit}
    `,
  },
  Left: {
    Gauge: styled.div`
      ${flex}
      flex-basis: calc(50% - 10px);
      flex-direction: column;
      justify-items: center;
      gap: 5px;
    `,
    Title: styled(Typography)`
      ${title}
    `,
    Value: styled(Typography)`
      font-size: 27px;
      line-height: 49px;
      white-space: nowrap;
      ${(p) => p.theme.breakpoints.up("md")} {
        font-size: 39px;
      }
    `,
    Unit: styled(Typography)`
      ${unit}
    `,
  },
  ValueWrap: styled.div`
    display: flex;
    justify-content: center;
  `,
}

type VehiclePerformanceGaugesProps = {
  data: api.VehiclePerformance
}

const getContent = (gauge: api.VehiclePerformanceGauge) => {
  const title = gauge.title
  const measurementUnits = gauge.measurementUnits
  if (gauge.contentA)
    return {
      title,
      measurementUnits,
      Value: gauge.contentA,
      ExtraValue: gauge.contentB ? gauge.contentB : null,
    }
  if (gauge.contentB)
    return {
      title,
      measurementUnits,
      Value: gauge.contentB,
      ExtraValue: null,
    }

  return null
}

const VehiclePerformanceGauges = ({ data }: VehiclePerformanceGaugesProps) => {
  const content = {
    Up: getContent(data.vehiclePerformanceGaugeUp),
    Right: getContent(data.vehiclePerformanceGaugeRight),
    Left: getContent(data.vehiclePerformanceGaugeLeft),
  }

  const hidden =
    content.Up == null && content.Left == null && content.Right == null

  return hidden ? null : (
    <AnimatedNumberProvider>
      <Styled.Wrapper>
        {content.Up != null && (
          <>
            <Styled.Up.Gauge>
              <Styled.Up.Title>{content.Up.title}</Styled.Up.Title>
              {content.Up.ExtraValue != null && (
                <Styled.Up.Value>
                  <AnimatedNumber value={content.Up.ExtraValue} />
                  <span className="slash">{"/"}</span>
                </Styled.Up.Value>
              )}
              <Styled.Up.Value>
                <b>
                  <AnimatedNumber value={content.Up.Value} />
                </b>
              </Styled.Up.Value>
              <Styled.Up.Unit>{content.Up.measurementUnits}</Styled.Up.Unit>
            </Styled.Up.Gauge>
            <Styled.Divider />
          </>
        )}
        <Styled.bottomWrapper>
          {content.Right != null && (
            <Styled.Right.Gauge>
              <Styled.Right.Title>{content.Right.title}</Styled.Right.Title>
              <Styled.ValueWrap>
                {content.Right.ExtraValue != null && (
                  <Styled.Right.Value>
                    <AnimatedNumber
                      value={content.Right.ExtraValue}
                      maximumFractionDigits={2}
                    />
                    <span className="slash">{"/"}</span>
                  </Styled.Right.Value>
                )}
                <Styled.Right.Value>
                  <AnimatedNumber
                    value={content.Right.Value}
                    maximumFractionDigits={2}
                  />
                </Styled.Right.Value>
              </Styled.ValueWrap>
              <Styled.Right.Unit>
                {content.Right.measurementUnits}
              </Styled.Right.Unit>
            </Styled.Right.Gauge>
          )}
          {content.Left != null && (
            <Styled.Left.Gauge>
              <Styled.Left.Title>{content.Left.title}</Styled.Left.Title>
              <Styled.ValueWrap>
                {content.Left.ExtraValue != null && (
                  <Styled.Left.Value>
                    <AnimatedNumber
                      value={content.Left.ExtraValue}
                      maximumFractionDigits={2}
                    />
                    <span className="slash">{"/"}</span>
                  </Styled.Left.Value>
                )}
                <Styled.Left.Value>
                  <AnimatedNumber
                    value={content.Left.Value}
                    maximumFractionDigits={2}
                  />
                </Styled.Left.Value>
              </Styled.ValueWrap>
              <Styled.Left.Unit>
                {content.Left.measurementUnits}
              </Styled.Left.Unit>
            </Styled.Left.Gauge>
          )}
        </Styled.bottomWrapper>
      </Styled.Wrapper>
    </AnimatedNumberProvider>
  )
}

export default memo(VehiclePerformanceGauges)
